// /onboard — 60-sec onboarding wizard.
// 5 steps via chassis state machine. Each step renders directly into the route host.

import type { RouteRenderer } from "../types.ts";
import {
  loadState as loadOnboardingState,
  saveState as saveOnboardingState,
  pickLanguage,
  enrollBiometric,
  setFamilyContacts,
  setEmergencyContacts,
  setMedicalInfo,
  backStep as onboardingBack,
  stepProgressRatio,
  validatePhone,
  type OnboardingState,
  type FamilyContact,
  type EmergencyContact,
} from "../chassis/elder/onboarding.ts";
import type { LangCode } from "../chassis/elder/types.ts";
import { setSelectedLang, makeBilingual, screenReaderAnnounce } from "../chassis/elder/index.ts";
import { i18n } from "../chassis/elder/i18n-strings.ts";
import { navigate } from "../router.ts";

export const renderOnboard: RouteRenderer = (host) => {
  let state = loadOnboardingState();
  const shell = document.createElement("div");
  shell.className = "sarathi-route-shell";

  const headerEl = document.createElement("header");
  const back = document.createElement("button");
  back.type = "button";
  back.style.cssText = "min-width:88px;min-height:88px;font-size:32px;background:transparent;border:3px solid var(--sarathi-ink);border-radius:14px;cursor:pointer;font-weight:700";
  back.textContent = "←";
  back.addEventListener("click", () => {
    if (state.step === "language") {
      navigate("home");
    } else {
      state = onboardingBack(state);
      saveOnboardingState(state);
      render();
    }
  });
  const headingWrap = document.createElement("div");
  const h1 = document.createElement("h1");
  h1.id = "sarathi-onboard-title";
  const sub = document.createElement("span");
  sub.className = "secondary";
  sub.id = "sarathi-onboard-sub";
  headingWrap.appendChild(h1);
  headingWrap.appendChild(sub);
  headerEl.appendChild(back);
  headerEl.appendChild(headingWrap);
  shell.appendChild(headerEl);

  const progress = document.createElement("div");
  progress.className = "sarathi-progress";
  progress.innerHTML = `<div></div>`;
  shell.appendChild(progress);

  const stage = document.createElement("div");
  shell.appendChild(stage);

  function render(): void {
    h1.textContent = stepTitle(state).primary;
    sub.textContent = stepTitle(state).secondary;
    const bar = progress.querySelector<HTMLElement>("div");
    if (bar) bar.style.width = `${Math.round(stepProgressRatio(state) * 100)}%`;
    stage.innerHTML = "";
    switch (state.step) {
      case "language": stage.appendChild(renderLanguage()); break;
      case "biometric": stage.appendChild(renderBiometric()); break;
      case "family-contacts": stage.appendChild(renderFamily()); break;
      case "emergency-contacts": stage.appendChild(renderEmergency()); break;
      case "medical-info": stage.appendChild(renderMedical()); break;
      case "complete": stage.appendChild(renderComplete()); break;
    }
  }

  function stepTitle(s: OnboardingState): { primary: string; secondary: string } {
    const lang = s.lang;
    switch (s.step) {
      case "language":
        return { primary: makeBilingual(i18n, "onboard.step.language", lang).primary, secondary: makeBilingual(i18n, "onboard.step.language", "en").primary };
      case "biometric":
        return { primary: makeBilingual(i18n, "onboard.step.biometric", lang).primary, secondary: makeBilingual(i18n, "onboard.step.biometric", "en").primary };
      case "family-contacts":
        return { primary: makeBilingual(i18n, "onboard.step.family", lang).primary, secondary: makeBilingual(i18n, "onboard.step.family", "en").primary };
      case "emergency-contacts":
        return { primary: makeBilingual(i18n, "onboard.step.emergency", lang).primary, secondary: makeBilingual(i18n, "onboard.step.emergency", "en").primary };
      case "medical-info":
        return { primary: makeBilingual(i18n, "onboard.step.medical", lang).primary, secondary: makeBilingual(i18n, "onboard.step.medical", "en").primary };
      case "complete":
        return { primary: makeBilingual(i18n, "onboard.complete", lang).primary, secondary: makeBilingual(i18n, "onboard.complete", "en").primary };
    }
  }

  function renderLanguage(): HTMLElement {
    const wrap = document.createElement("div");
    wrap.className = "sarathi-card";
    wrap.innerHTML = `<p style="margin:0 0 18px;font-size:19px;color:var(--sarathi-ink)">${escapeHtml(makeBilingual(i18n, "onboard.welcome", state.lang).primary)}</p>`;
    const grid = document.createElement("div");
    grid.style.cssText = "display:grid;grid-template-columns:repeat(auto-fit,minmax(140px,1fr));gap:12px;margin-top:8px";
    const langs: ReadonlyArray<{ code: LangCode; native: string; en: string }> = [
      { code: "hi", native: "हिन्दी", en: "Hindi" },
      { code: "ta", native: "தமிழ்", en: "Tamil" },
      { code: "bn", native: "বাংলা", en: "Bengali" },
      { code: "mr", native: "मराठी", en: "Marathi" },
      { code: "gu", native: "ગુજરાતી", en: "Gujarati" },
      { code: "en", native: "English", en: "English" },
    ];
    for (const l of langs) {
      const btn = document.createElement("button");
      btn.type = "button";
      btn.style.cssText = `min-height:108px;padding:12px;background:${state.lang === l.code ? "var(--sarathi-saffron)" : "#fff"};color:${state.lang === l.code ? "#fff" : "var(--sarathi-ink)"};border:3px solid ${state.lang === l.code ? "var(--sarathi-saffron-dark)" : "var(--sarathi-rule)"};border-radius:14px;font-size:24px;font-weight:700;cursor:pointer;font-family:inherit`;
      btn.innerHTML = `${escapeHtml(l.native)}<span style="display:block;font-size:15px;font-weight:500;margin-top:4px;opacity:.82">${escapeHtml(l.en)}</span>`;
      btn.addEventListener("click", () => {
        setSelectedLang(l.code);
        state = pickLanguage(state, l.code);
        saveOnboardingState(state);
        screenReaderAnnounce(`Language set to ${l.en}`, { polite: true, clearAfterMs: 3500 });
        render();
      });
      grid.appendChild(btn);
    }
    wrap.appendChild(grid);
    return wrap;
  }

  function renderBiometric(): HTMLElement {
    const wrap = document.createElement("div");
    wrap.className = "sarathi-card";
    wrap.innerHTML = `<p style="margin:0 0 18px;font-size:19px;color:var(--sarathi-ink)">Aapka phone ko aapki ungli ya chehre se khulna chahiye.</p>`;
    const row = document.createElement("div");
    row.className = "sarathi-row";
    const enableBtn = document.createElement("button");
    enableBtn.type = "button";
    enableBtn.style.cssText = "min-height:96px;background:var(--sarathi-saffron);color:#fff;border:3px solid var(--sarathi-saffron-dark);border-radius:14px;font-size:22px;font-weight:700;cursor:pointer";
    enableBtn.textContent = "Ungli / Chehra lock chalu karein";
    enableBtn.addEventListener("click", async () => {
      let kind: OnboardingState["biometricKind"] = "device-pin";
      try {
        if (typeof window !== "undefined" && "PublicKeyCredential" in window) {
          const supported = await (window as unknown as { PublicKeyCredential: { isUserVerifyingPlatformAuthenticatorAvailable?: () => Promise<boolean> } }).PublicKeyCredential.isUserVerifyingPlatformAuthenticatorAvailable?.();
          if (supported) kind = "webauthn";
        }
      } catch {
        kind = "device-pin";
      }
      state = enrollBiometric(state, kind);
      saveOnboardingState(state);
      render();
    });
    const skipBtn = document.createElement("button");
    skipBtn.type = "button";
    skipBtn.style.cssText = "min-height:96px;background:transparent;color:var(--sarathi-ink);border:3px solid var(--sarathi-ink);border-radius:14px;font-size:22px;font-weight:700;cursor:pointer";
    skipBtn.textContent = makeBilingual(i18n, "onboard.skip", state.lang).primary;
    skipBtn.addEventListener("click", () => {
      state = enrollBiometric(state, "none");
      saveOnboardingState(state);
      render();
    });
    row.appendChild(enableBtn);
    row.appendChild(skipBtn);
    wrap.appendChild(row);
    return wrap;
  }

  function renderFamily(): HTMLElement {
    const wrap = document.createElement("div");
    wrap.className = "sarathi-card";
    const note = document.createElement("p");
    note.style.cssText = "margin:0 0 14px;font-size:18px";
    note.textContent = "Add up to 5 family members. Each can be allowed to mirror screen, send pins, or just be alerted.";
    wrap.appendChild(note);
    const list = document.createElement("div");
    list.id = "sarathi-onboard-family-list";
    list.style.cssText = "display:flex;flex-direction:column;gap:14px;margin-bottom:18px";
    let draft: FamilyContact[] = [...state.familyContacts];
    const renderList = (): void => {
      list.innerHTML = "";
      if (draft.length === 0) {
        const empty = document.createElement("p");
        empty.style.cssText = "color:var(--sarathi-ash);font-size:17px;margin:0";
        empty.textContent = "Abhi koi nahin jodha gaya.";
        list.appendChild(empty);
      } else {
        for (let idx = 0; idx < draft.length; idx++) {
          const c = draft[idx];
          if (!c) continue;
          const row = document.createElement("div");
          row.style.cssText = "display:flex;justify-content:space-between;align-items:center;gap:14px;padding:14px;background:var(--sarathi-cream-2);border-radius:12px";
          row.innerHTML = `<div><div style="font-size:19px;font-weight:700">${escapeHtml(c.name)} <span style="font-size:14px;color:var(--sarathi-ash);font-weight:500;text-transform:uppercase;letter-spacing:.1em">${escapeHtml(c.relation)}</span></div><div style="font-size:16px;color:var(--sarathi-ash)">${escapeHtml(c.phone)} · mirror:${c.canMirror ? "Y" : "N"} · pin:${c.canPin ? "Y" : "N"}</div></div>`;
          const rm = document.createElement("button");
          rm.type = "button";
          rm.style.cssText = "min-height:56px;padding:0 18px;background:transparent;color:var(--sarathi-danger);border:2px solid var(--sarathi-danger);border-radius:12px;font-size:16px;font-weight:700;cursor:pointer";
          rm.textContent = "Hatayein";
          rm.addEventListener("click", () => {
            draft = draft.filter((_, i) => i !== idx);
            renderList();
          });
          row.appendChild(rm);
          list.appendChild(row);
        }
      }
    };
    renderList();
    wrap.appendChild(list);

    const form = document.createElement("form");
    form.style.cssText = "display:grid;grid-template-columns:1fr 1fr;gap:12px";
    form.innerHTML = `
      <input name="name" placeholder="Name" required maxlength="40" style="min-height:60px;font-size:18px;border:2px solid var(--sarathi-rule);border-radius:12px;padding:10px;font-family:inherit">
      <input name="phone" placeholder="+91 9XXXXXXXXX" required style="min-height:60px;font-size:18px;border:2px solid var(--sarathi-rule);border-radius:12px;padding:10px;font-family:inherit">
      <select name="relation" style="min-height:60px;font-size:18px;border:2px solid var(--sarathi-rule);border-radius:12px;padding:10px;font-family:inherit">
        <option value="son">Son</option><option value="daughter">Daughter</option><option value="spouse">Spouse</option><option value="sibling">Sibling</option><option value="friend">Friend</option><option value="other">Other</option>
      </select>
      <label style="display:flex;align-items:center;gap:8px;font-size:16px"><input type="checkbox" name="canMirror" checked style="width:24px;height:24px"> Mirror</label>
      <label style="display:flex;align-items:center;gap:8px;font-size:16px"><input type="checkbox" name="canPin" checked style="width:24px;height:24px"> Pin</label>
      <label style="display:flex;align-items:center;gap:8px;font-size:16px"><input type="checkbox" name="canAlert" checked style="width:24px;height:24px"> Alert</label>
      <button type="submit" style="grid-column:1/-1;min-height:72px;background:var(--sarathi-saffron);color:#fff;border:3px solid var(--sarathi-saffron-dark);border-radius:14px;font-size:20px;font-weight:700;cursor:pointer">+ Add this person</button>
    `;
    form.addEventListener("submit", (e) => {
      e.preventDefault();
      const fd = new FormData(form);
      const phoneStr = String(fd.get("phone") ?? "").trim();
      if (!validatePhone(phoneStr)) {
        alert("Phone number does not look valid. Use 10-digit Indian mobile or short code.");
        return;
      }
      const next: FamilyContact = {
        name: String(fd.get("name") ?? "").trim().slice(0, 40),
        phone: phoneStr,
        relation: (String(fd.get("relation") ?? "other") as FamilyContact["relation"]),
        canMirror: fd.get("canMirror") === "on",
        canPin: fd.get("canPin") === "on",
        canAlert: fd.get("canAlert") === "on",
      };
      if (draft.length >= 5) {
        alert("Up to 5 family members only. Remove someone first.");
        return;
      }
      draft.push(next);
      form.reset();
      renderList();
    });
    wrap.appendChild(form);

    const next = document.createElement("button");
    next.type = "button";
    next.style.cssText = "margin-top:24px;min-height:96px;width:100%;background:var(--sarathi-ink);color:#fff;border:3px solid var(--sarathi-ink);border-radius:14px;font-size:22px;font-weight:700;cursor:pointer";
    next.textContent = `${makeBilingual(i18n, "onboard.next", state.lang).primary} · ${makeBilingual(i18n, "onboard.next", "en").primary}`;
    next.addEventListener("click", () => {
      if (draft.length === 0) {
        alert("Please add at least one family member to continue.");
        return;
      }
      state = setFamilyContacts(state, draft);
      saveOnboardingState(state);
      render();
    });
    wrap.appendChild(next);
    return wrap;
  }

  function renderEmergency(): HTMLElement {
    const wrap = document.createElement("div");
    wrap.className = "sarathi-card";
    let draft: EmergencyContact[] = [...state.emergencyContacts];
    const list = document.createElement("div");
    list.style.cssText = "display:flex;flex-direction:column;gap:12px;margin-bottom:18px";
    const refresh = (): void => {
      list.innerHTML = "";
      for (let idx = 0; idx < draft.length; idx++) {
        const c = draft[idx];
        if (!c) continue;
        const row = document.createElement("div");
        row.style.cssText = "display:flex;justify-content:space-between;align-items:center;gap:12px;padding:14px;background:var(--sarathi-cream-2);border-radius:12px";
        row.innerHTML = `<div><div style="font-size:19px;font-weight:700">${escapeHtml(c.label)}</div><div style="font-size:17px;color:var(--sarathi-ink);font-weight:600;font-family:monospace">${escapeHtml(c.phone)}</div></div>`;
        if (c.phone !== "112") {
          const rm = document.createElement("button");
          rm.type = "button";
          rm.style.cssText = "min-height:54px;padding:0 16px;background:transparent;color:var(--sarathi-danger);border:2px solid var(--sarathi-danger);border-radius:12px;font-size:16px;font-weight:700;cursor:pointer";
          rm.textContent = "Hatayein";
          rm.addEventListener("click", () => {
            draft = draft.filter((_, i) => i !== idx);
            refresh();
          });
          row.appendChild(rm);
        } else {
          const locked = document.createElement("span");
          locked.style.cssText = "font-size:13px;color:var(--sarathi-ash);letter-spacing:.12em;text-transform:uppercase;font-weight:600";
          locked.textContent = "Locked";
          row.appendChild(locked);
        }
        list.appendChild(row);
      }
    };
    refresh();
    wrap.appendChild(list);
    const form = document.createElement("form");
    form.style.cssText = "display:grid;grid-template-columns:1fr 1fr;gap:12px";
    form.innerHTML = `
      <input name="label" placeholder="Label (Family doctor, neighbor)" required maxlength="32" style="min-height:60px;font-size:18px;border:2px solid var(--sarathi-rule);border-radius:12px;padding:10px;font-family:inherit">
      <input name="phone" placeholder="Phone or short code" required style="min-height:60px;font-size:18px;border:2px solid var(--sarathi-rule);border-radius:12px;padding:10px;font-family:inherit">
      <button type="submit" style="grid-column:1/-1;min-height:72px;background:var(--sarathi-saffron);color:#fff;border:3px solid var(--sarathi-saffron-dark);border-radius:14px;font-size:20px;font-weight:700;cursor:pointer">+ Add contact</button>
    `;
    form.addEventListener("submit", (e) => {
      e.preventDefault();
      const fd = new FormData(form);
      const phone = String(fd.get("phone") ?? "").trim();
      if (!validatePhone(phone)) {
        alert("Phone number does not look valid.");
        return;
      }
      if (draft.length >= 6) {
        alert("6 maximum.");
        return;
      }
      const priority = (Math.min(3, draft.length + 1) as 1 | 2 | 3);
      draft.push({ label: String(fd.get("label") ?? "").trim().slice(0, 32), phone, priority });
      form.reset();
      refresh();
    });
    wrap.appendChild(form);
    const next = document.createElement("button");
    next.type = "button";
    next.style.cssText = "margin-top:24px;min-height:96px;width:100%;background:var(--sarathi-ink);color:#fff;border:3px solid var(--sarathi-ink);border-radius:14px;font-size:22px;font-weight:700;cursor:pointer";
    next.textContent = `${makeBilingual(i18n, "onboard.next", state.lang).primary} · ${makeBilingual(i18n, "onboard.next", "en").primary}`;
    next.addEventListener("click", () => {
      state = setEmergencyContacts(state, draft);
      saveOnboardingState(state);
      render();
    });
    wrap.appendChild(next);
    return wrap;
  }

  function renderMedical(): HTMLElement {
    const wrap = document.createElement("div");
    wrap.className = "sarathi-card";
    wrap.innerHTML = `
      <p style="margin:0 0 14px;font-size:18px;color:var(--sarathi-ash)">Sab kuch optional hai. Aapko jo dena ho de dijiye. Sirf aapatkaal mein kaam aayega.</p>
      <form id="sarathi-medical-form" style="display:flex;flex-direction:column;gap:14px">
        <label style="font-size:18px;font-weight:700">Blood group <input name="bloodGroup" maxlength="3" placeholder="B+, O-, AB+, ..." style="display:block;width:160px;min-height:60px;font-size:20px;border:2px solid var(--sarathi-rule);border-radius:12px;padding:10px;font-family:inherit"></label>
        <label style="font-size:18px;font-weight:700">Chronic conditions (comma sep) <input name="chronic" placeholder="Diabetes, BP, asthma" style="display:block;width:100%;min-height:60px;font-size:18px;border:2px solid var(--sarathi-rule);border-radius:12px;padding:10px;font-family:inherit"></label>
        <label style="font-size:18px;font-weight:700">Allergies (comma sep) <input name="allergies" placeholder="Penicillin, peanuts, ..." style="display:block;width:100%;min-height:60px;font-size:18px;border:2px solid var(--sarathi-rule);border-radius:12px;padding:10px;font-family:inherit"></label>
        <label style="font-size:18px;font-weight:700">Current meds (comma sep) <input name="meds" placeholder="Metformin 500, Telma 40" style="display:block;width:100%;min-height:60px;font-size:18px;border:2px solid var(--sarathi-rule);border-radius:12px;padding:10px;font-family:inherit"></label>
        <label style="font-size:18px;font-weight:700">Preferred doctor phone <input name="docPhone" placeholder="+91 9XXXXXXXXX" style="display:block;width:100%;min-height:60px;font-size:18px;border:2px solid var(--sarathi-rule);border-radius:12px;padding:10px;font-family:inherit"></label>
        <label style="display:flex;gap:8px;align-items:center;font-size:18px"><input type="checkbox" name="abha" style="width:26px;height:26px"> ABHA already linked</label>
        <button type="submit" style="margin-top:14px;min-height:96px;background:var(--sarathi-ink);color:#fff;border:3px solid var(--sarathi-ink);border-radius:14px;font-size:22px;font-weight:700;cursor:pointer">Finish setup</button>
      </form>
    `;
    const form = wrap.querySelector<HTMLFormElement>("#sarathi-medical-form");
    form?.addEventListener("submit", (e) => {
      e.preventDefault();
      const fd = new FormData(form);
      const docPhoneStr = String(fd.get("docPhone") ?? "").trim();
      state = setMedicalInfo(state, {
        bloodGroup: String(fd.get("bloodGroup") ?? "").trim() || null,
        chronicConditions: splitCsv(String(fd.get("chronic") ?? "")),
        allergies: splitCsv(String(fd.get("allergies") ?? "")),
        currentMeds: splitCsv(String(fd.get("meds") ?? "")),
        preferredPhysicianPhone: docPhoneStr || null,
        abhaLinked: fd.get("abha") === "on",
      });
      saveOnboardingState(state);
      render();
    });
    return wrap;
  }

  function renderComplete(): HTMLElement {
    const wrap = document.createElement("div");
    wrap.className = "sarathi-card";
    wrap.style.textAlign = "center";
    wrap.innerHTML = `
      <p style="font-size:64px;line-height:1;margin:0 0 16px" aria-hidden="true">✓</p>
      <h2 style="font-family:'Crimson Pro',serif;font-size:30px;margin:0 0 10px">${escapeHtml(makeBilingual(i18n, "onboard.complete", state.lang).primary)}</h2>
      <p style="font-size:18px;color:var(--sarathi-ash);margin:0 0 24px">${escapeHtml(makeBilingual(i18n, "onboard.complete", "en").primary)}</p>
      <button type="button" id="sarathi-onboard-go-home" style="min-height:96px;padding:0 36px;background:var(--sarathi-saffron);color:#fff;border:3px solid var(--sarathi-saffron-dark);border-radius:14px;font-size:22px;font-weight:700;cursor:pointer">Aapka home screen kholain</button>
    `;
    wrap.querySelector<HTMLButtonElement>("#sarathi-onboard-go-home")?.addEventListener("click", () => navigate("home"));
    return wrap;
  }

  host.appendChild(shell);
  render();

  return () => shell.remove();
};

function splitCsv(s: string): readonly string[] {
  return s.split(",").map((p) => p.trim()).filter((p) => p.length > 0);
}

function escapeHtml(s: string): string {
  return s.replace(/[&<>"']/g, (c) => {
    const map: Record<string, string> = { "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" };
    return map[c] ?? c;
  });
}
